import { useTranslation } from '@jq4race/i18n';
import { isPaymentDue, type TicketStatus } from '@jq4race/shared';
import { cn } from '@jq4race/ui';
import { BadgeCheck, Banknote, DoorOpen, Gift } from 'lucide-react';

import { formatWaitTime } from '../../../pickup-queue/domain/format-wait-time';

interface SearchResultRowProps {
  id: string;
  ticketNumber: number;
  plate: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  /**
   * valet-keychain-qr — keychain-visibility-search: the ticket's keychain QR
   * code when present (null/absent → no placeholder/error, just omitted).
   */
  qrTag?: string | null;
  status: TicketStatus;
  elapsedSeconds: number;
  /**
   * JQ4-54 (JQ4-38 follow-up) — payment status. Gates the cash-payment button:
   * it is only shown for parked + unpaid tickets (the only reachable payable state).
   */
  paymentStatus?: 'unpaid' | 'paid';
  /**
   * QA R3-10 — server-computed; `false` when the ticket is 'unpaid' but its total is $0, in
   * which case no cash/comp action is offered (the server would reject it). Absent = required.
   */
  paymentRequired?: boolean;
  /**
   * frequent-customer-memberships S4 — true when the ticket is covered by an
   * active membership. Renders a "MEMBER" badge so covered vehicles (which show
   * as paymentStatus='paid', otherwise indistinguishable from a cash-paid
   * ticket) are recognizable at a glance and never charged.
   */
  coveredByMembership?: boolean;
  /**
   * JQ4-17 · US-CONFIG-003 — Optional callback for navigating to the hotel
   * exits page. When provided, a button is shown for parked tickets.
   * The routing concern stays in the caller (SearchResultList) to avoid
   * coupling this presentational component to the router context.
   */
  onExitsClick?: () => void;
  /**
   * JQ4-54 (JQ4-38 follow-up) — Optional callback to open the cash-payment dialog.
   * When provided AND the ticket is parked + unpaid, a "Cash" button is shown.
   * The mutation/dialog concern stays in the caller (SearchPage).
   */
  onRegisterCash?: () => void;
  /**
   * JQ4-19 slice 3 — Optional callback to open the comp dialog.
   * Button shown only when compsAuthorized=true AND ticket is parked + unpaid (Scenario 5).
   */
  onApplyComp?: () => void;
  /**
   * JQ4-19 slice 3 — Whether comps are enabled at this location AND the actor is authorized.
   * Controls visibility of the comp button (Scenario 5).
   */
  compsAuthorized?: boolean;
}

const STATUS_LABEL_KEY: Partial<Record<TicketStatus, string>> = {
  parked: 'status.parked',
  requested: 'status.requested',
  in_transit: 'status.inTransit',
};

const STATUS_COLORS: Partial<Record<TicketStatus, { colorClass: string; bgClass: string }>> = {
  parked: { colorClass: 'text-success', bgClass: 'bg-success/15' },
  requested: { colorClass: 'text-info', bgClass: 'bg-info/15' },
  in_transit: { colorClass: 'text-warning', bgClass: 'bg-warning/15' },
};

function buildName(firstName: string | null, lastName: string | null): string | null {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

export function SearchResultRow({
  ticketNumber,
  plate,
  firstName,
  lastName,
  phone,
  qrTag,
  status,
  elapsedSeconds,
  paymentStatus,
  paymentRequired,
  coveredByMembership = false,
  onExitsClick,
  onRegisterCash,
  onApplyComp,
  compsAuthorized = false,
}: SearchResultRowProps) {
  const { t } = useTranslation('search');
  const statusLabelKey = STATUS_LABEL_KEY[status];
  const label = statusLabelKey ? t(statusLabelKey) : status;
  const { colorClass, bgClass } = STATUS_COLORS[status] ?? {
    colorClass: 'text-muted-foreground',
    bgClass: 'bg-muted/30',
  };
  const name = buildName(firstName, lastName);
  const paymentDue = isPaymentDue({ paymentStatus, paymentRequired });

  return (
    <article
      className={cn(
        'flex min-h-[56px] w-full items-center justify-between gap-3',
        'rounded-sm border border-border bg-card px-4 py-3.5',
      )}
      aria-label={`Ticket ${ticketNumber} · ${plate}`}
    >
      {/* Left: plate (prominent) + meta */}
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="jq-mono jq-h6 text-jq-gold-bright">{plate}</span>
        <span className="jq-body-sm text-muted-foreground">
          #{ticketNumber}
          {name ? ` · ${name}` : ''} · {formatWaitTime(elapsedSeconds)}
        </span>
        {phone ? <span className="jq-body-sm text-muted-foreground/70">{phone}</span> : null}
        {/* valet-keychain-qr — keychain code shown only when present, no placeholder when null */}
        {qrTag ? (
          <span className="jq-mono jq-body-sm text-muted-foreground/70">{qrTag}</span>
        ) : null}
      </div>

      {/* Right: status pill + optional hotel exits action */}
      <div className="flex shrink-0 items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1',
            'font-sans text-[12px] font-medium leading-none tracking-snug',
            bgClass,
            colorClass,
          )}
        >
          {label}
        </span>

        {/* frequent-customer-memberships S4 — MEMBER badge (covered ticket = do not charge) */}
        {coveredByMembership ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2.5 py-1',
              'font-sans text-[12px] font-medium leading-none tracking-snug',
              'bg-emerald-600/15 text-emerald-600',
            )}
          >
            <BadgeCheck className="size-3 shrink-0" aria-hidden />
            {t('member')}
          </span>
        ) : null}

        {/* JQ4-54 (JQ4-38 follow-up) — Cash payment button (parked + unpaid only, caller wires dialog) */}
        {status === 'parked' && paymentDue && onRegisterCash ? (
          <button
            type="button"
            onClick={onRegisterCash}
            aria-label={t('cashAria', { ticketNumber, plate })}
            className={cn(
              'inline-flex items-center gap-1 rounded-sm px-2 py-1.5',
              'bg-warning text-warning-foreground font-sans text-[11px] font-medium',
              'hover:bg-warning/90 transition-colors',
            )}
          >
            <Banknote className="size-3 shrink-0" aria-hidden />
            {t('cash')}
          </button>
        ) : null}

        {/* JQ4-19 slice 3 — Comp (cortesía) button (parked + unpaid + compsAuthorized, Scenario 5) */}
        {status === 'parked' && paymentDue && compsAuthorized && onApplyComp ? (
          <button
            type="button"
            onClick={onApplyComp}
            aria-label={t('compAria', { ticketNumber, plate })}
            className={cn(
              'inline-flex items-center gap-1 rounded-sm px-2 py-1.5',
              'bg-info text-info-foreground font-sans text-[11px] font-medium',
              'hover:bg-info/90 transition-colors',
            )}
          >
            <Gift className="size-3 shrink-0" aria-hidden />
            {t('comp')}
          </button>
        ) : null}

        {/* JQ4-17 · US-CONFIG-003 — Hotel exits button (parked tickets only, caller wires nav) */}
        {status === 'parked' && onExitsClick ? (
          <button
            type="button"
            onClick={onExitsClick}
            aria-label={t('exitsAria', { ticketNumber, plate })}
            className={cn(
              'inline-flex items-center gap-1 rounded-sm px-2 py-1.5',
              'bg-muted text-muted-foreground font-sans text-[11px] font-medium',
              'hover:bg-muted/80 transition-colors',
            )}
          >
            <DoorOpen className="size-3 shrink-0" aria-hidden />
            {t('exits')}
          </button>
        ) : null}
      </div>
    </article>
  );
}
